/**
 * Deals & Promotions Tabs Section
 * Handles tab navigation and content switching
 */

class DealsTabsSection {
  constructor(container) {
    this.container = container;
    this.tabs = this.container.querySelectorAll('.deals-tabs__nav-item');
    this.descriptions = this.container.querySelectorAll('.deals-tabs__description');
    this.panels = this.container.querySelectorAll('.deals-tabs__panel');
    this.nav = this.container.querySelector('.deals-tabs__nav');
    this.activeIndex = 0;
    this.isMobile = false;
    
    if (this.tabs.length === 0) {
      console.warn('DealsTabsSection: No tabs found');
      return;
    }
    
    this.init();
    this.checkViewport();
    this.setupResizeObserver();
  }
  
  init() {
    // Add click handlers to all tabs
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
      
      // Keyboard navigation
      tab.addEventListener('keydown', (e) => this.handleKeyboard(e, index));
    });
    
    // Mobile carousel navigation
    this.setupMobileNavigation();
  }
  
  setupMobileNavigation() {
    if (!this.nav) return;
    
    // Click on ::before (left arrow)
    this.nav.addEventListener('click', (e) => {
      const rect = this.nav.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      
      // Check if click is in the left arrow area (first 25px)
      if (clickX < 25 && this.isMobile) {
        this.navigatePrevious();
      }
      // Check if click is in the right arrow area (last 25px)
      else if (clickX > rect.width - 25 && this.isMobile) {
        this.navigateNext();
      }
    });
  }
  
  navigatePrevious() {
    const newIndex = this.activeIndex === 0 ? this.tabs.length - 1 : this.activeIndex - 1;
    this.switchTab(newIndex);
  }
  
  navigateNext() {
    const newIndex = this.activeIndex === this.tabs.length - 1 ? 0 : this.activeIndex + 1;
    this.switchTab(newIndex);
  }
  
  checkViewport() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    
    // Update height if switching to/from mobile or on initial load
    if (this.isMobile && this.panels[this.activeIndex]) {
      this.updatePanelContainerHeight(this.panels[this.activeIndex]);
    } else if (!this.isMobile && wasMobile) {
      // Reset height when switching back to desktop
      const panelsContainer = this.container.querySelector('.deals-tabs__panels');
      if (panelsContainer) {
        panelsContainer.style.minHeight = '';
      }
    }
  }
  
  setupResizeObserver() {
    window.addEventListener('resize', () => {
      this.checkViewport();
    });
  }
  
  handleKeyboard(event, currentIndex) {
    let newIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex === 0 ? this.tabs.length - 1 : currentIndex - 1;
        break;
        
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex === this.tabs.length - 1 ? 0 : currentIndex + 1;
        break;
        
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
        
      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
        
      default:
        return;
    }
    
    this.switchTab(newIndex);
    this.tabs[newIndex].focus();
  }
  
  switchTab(index) {
    // Prevent unnecessary switches
    if (index === this.activeIndex || index < 0 || index >= this.tabs.length) {
      return;
    }
    
    // Update previous tab
    this.deactivateTab(this.activeIndex);
    
    // Update new tab
    this.activateTab(index);
    
    // Update active index
    this.activeIndex = index;
  }
  
  deactivateTab(index) {
    // Update tab button
    this.tabs[index].classList.remove('is-active');
    this.tabs[index].setAttribute('aria-selected', 'false');
    this.tabs[index].setAttribute('tabindex', '-1');
    
    // Update description
    if (this.descriptions[index]) {
      this.descriptions[index].classList.remove('is-active');
    }
    
    // Update panel
    if (this.panels[index]) {
      this.panels[index].classList.remove('is-active');
    }
  }
  
  activateTab(index) {
    // Update tab button
    this.tabs[index].classList.add('is-active');
    this.tabs[index].setAttribute('aria-selected', 'true');
    this.tabs[index].setAttribute('tabindex', '0');
    
    // Update description
    if (this.descriptions[index]) {
      this.descriptions[index].classList.add('is-active');
    }
    
    // Update panel
    if (this.panels[index]) {
      this.panels[index].classList.add('is-active');
      
      // Update container height on mobile for absolute positioned panels
      if (this.isMobile) {
        this.updatePanelContainerHeight(this.panels[index]);
      }
    }
  }
  
  updatePanelContainerHeight(activePanel) {
    const panelsContainer = this.container.querySelector('.deals-tabs__panels');
    if (!panelsContainer) return;
    
    // Get the height of the active panel
    const panelHeight = activePanel.scrollHeight;
    
    // Set the container height to match
    panelsContainer.style.minHeight = `${panelHeight}px`;
  }
}

// Initialize all tab sections on page load
function initDealsTabsSections() {
  const sections = document.querySelectorAll('[data-section-type="deals-tabs"]');
  
  sections.forEach(section => {
    try {
      new DealsTabsSection(section);
    } catch (error) {
      console.error('Error initializing DealsTabsSection:', error);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDealsTabsSections);
} else {
  initDealsTabsSections();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  // Handle section load in theme editor
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="deals-tabs"]');
    if (section) {
      try {
        new DealsTabsSection(section);
      } catch (error) {
        console.error('Error loading DealsTabsSection in theme editor:', error);
      }
    }
  });
  
  // Handle block selection in theme editor
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const section = block.closest('[data-section-type="deals-tabs"]');
    
    if (section && block.classList.contains('deals-tabs__panel')) {
      const index = parseInt(block.dataset.panelIndex, 10);
      const tabsInstance = section._dealsTabsInstance;
      
      if (tabsInstance && !isNaN(index)) {
        tabsInstance.switchTab(index);
      }
    }
  });
}

